import { GridColumnHeaders, GridFilterModel } from "@mui/x-data-grid-pro";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useFocusGridRow } from "../../../hooks/useFocusGridRow";
import MaintenanceRowDetail from "./MaintenanceRowDetail";
import ColumnMenu from "@/components/columnMenu/ColumnMenu";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import CustomToolbar from "@/components/grid/CustomToolbar";
import useMaintenanceColumns from "@/hooks/columns/useMaintenanceColumns";
import { useUpdateMaintenanceJob } from "@/hooks/maintenance/useUpdateMaintenanceWorkOrderJob";
import { MaintenanceDisplayData } from "@/models/MaintenanceDisplayData";

export interface MaintenanceGridProps {
  data?: MaintenanceDisplayData[];
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
}

export function MaintenanceGrid({ data, filterModel, setFilterModel }: MaintenanceGridProps) {
  const { apiRef, loading, gridState } = useDataGridSettings();
  const { initialColumns } = useMaintenanceColumns();
  const updateJob = useUpdateMaintenanceJob();

  useEffect(() => {
    if (!loading && gridState !== null) {
      apiRef.current?.restoreState(gridState);
      setFilterModel(gridState.filter?.filterModel ?? { items: [] });

      const jobIdMatch = window.location.hash.match(/job:([^,]+)/);

      const jobId = jobIdMatch
        ? jobIdMatch[1]
        : new URLSearchParams(window.location.search).get("job");
      if (!jobId) return;
      apiRef.current?.setFilterModel({
        items: [],
        quickFilterValues: [String(jobId)],
      });
      const rowId = data?.find(d => d.reference === jobId)?.id;
      if (!rowId) return;
      apiRef.current.setExpandedDetailPanels([rowId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridState, initialColumns, loading, setFilterModel]);

  const focusRow = useFocusGridRow(filterModel, "device.name");

  const processRowUpdate = async (
    newRow: MaintenanceDisplayData,
    oldRow: MaintenanceDisplayData,
  ) => {
    if (newRow.statusCode !== oldRow.statusCode) {
      updateJob.mutate({
        job: newRow.workOrderJob,
        newStatus: newRow.statusCode,
      });
      return newRow;
    }
  };

  return (
    <I18nGrid
      apiRef={apiRef}
      rows={data}
      columns={initialColumns}
      getRowId={row => row.id}
      loading={loading}
      slots={{
        // noRowsOverlay: NoRowsOverlay,
        columnHeaders: GridColumnHeaders,
        toolbar: CustomToolbar,
        columnMenu: ColumnMenu,
      }}
      filterModel={filterModel}
      onFilterModelChange={setFilterModel}
      onCellClick={focusRow}
      getDetailPanelHeight={() => "auto"}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={error => {
        console.error("Row update error:", error);
      }}
      getDetailPanelContent={({ row }) => <MaintenanceRowDetail row={row} />}
      disableRowSelectionOnClick={true}
      pagination
      pageSizeOptions={[10, 25, 50, 100]}
      sx={{
        "& .MuiDataGrid-detailPanel": {
          overflow: "visible",
        },
      }}
    />
  );
}
